import React from 'react';
import { Gavel as RuleIcon } from '@mui/icons-material';
import { Box, Card, CardActionArea, CardContent, Chip,Divider, Stack, Typography } from '@mui/material';

import type { ComplianceRuleDocument } from '../types';

interface ComplianceRuleCardProps {
  rule: ComplianceRuleDocument;
  isGlobal: boolean;
  isDisabled?: boolean;
  variant?: 'preview' | 'full';
  onClick?: () => void;
}

export const ComplianceRuleCard: React.FC<ComplianceRuleCardProps> = ({ 
  rule, 
  isGlobal,
  isDisabled = false,
  variant = 'full',
  onClick 
}) => {
  const isPreview = variant === 'preview';

  const CardWrapper = ({ children }: { children: React.ReactNode }) => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        opacity: isDisabled ? 0.6 : 1,
        '&:hover': onClick ? {
          boxShadow: 1,
          borderColor: 'borderColors.brand',
          cursor: 'pointer'
        } : undefined,
      }}
    >
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
          {children}
        </CardActionArea>
      ) : children}
    </Card>
  );

  return (
    <CardWrapper>
      {/* Header Section with Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <RuleIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.25}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                lineHeight: 1.25,
                color: 'textColors.darkText',
                // Clamp title lines in preview
                ...(isPreview && {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                })
              }}
            >
              {rule.title || rule.rule_text.slice(0, 50)}
            </Typography>
            {isDisabled && (
              <Chip 
                label="Disabled" 
                size="small" 
                sx={{ height: 16, fontSize: '0.625rem' }} 
                color="default"
                variant="outlined"
              />
            )}
            {!isDisabled && isGlobal && (
              <Chip 
                label="Global" 
                size="small" 
                sx={{ height: 16, fontSize: '0.625rem' }} 
                color="secondary"
                variant="outlined"
              />
            )}
            {!isDisabled && !isGlobal && (
              <Chip 
                label="Organization" 
                size="small" 
                sx={{ height: 16, fontSize: '0.625rem' }} 
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}>
            {rule.payer_type === 'specific' ? rule.applicable_payer_name : rule.payer_type}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', width: '100%' }}>
        <Stack spacing={1}>
          {/* Rule Text (Shortened for preview) */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.75rem', 
                lineHeight: 1.4, 
                color: 'textColors.darkText', 
                mb: 1,
                ...(isPreview && {
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                })
              }}
            >
              {rule.rule_text}
            </Typography>
          </Box>

          {/* In full mode, show all details. In preview, skip the rest */}
          {!isPreview && (
            <>
              {/* Criterion */}
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    color: 'textColors.lightText',
                    fontSize: '0.625rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.25,
                  }}
                >
                  Criterion
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
                  {rule.criterion}
                </Typography>
              </Box>

              {/* Compliance Rationale */}
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    color: 'textColors.lightText',
                    fontSize: '0.625rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.25,
                  }}
                >
                  Rationale
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
                  {rule.compliance_rationale}
                </Typography>
              </Box>

              {/* Format or Wording Required */}
              {rule.format_or_wording_required && rule.format_or_wording_required !== 'N/A' && (
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 600,
                      color: 'textColors.lightText',
                      fontSize: '0.625rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.25,
                    }}
                  >
                    Format Required
                  </Typography>
                  <Box
                    sx={{
                      p: 0.75,
                      borderRadius: 1,
                      bgcolor: 'backgroundColors.tertiary',
                      border: '1px solid',
                      borderColor: 'borderColors.lightprimary',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.6875rem',
                        lineHeight: 1.3,
                        fontFamily: 'monospace',
                        color: 'textColors.darkText',
                      }}
                    >
                      {rule.format_or_wording_required}
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Stack>
      </CardContent>

      {/* Footer with Source Information */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          mt: 'auto',
          bgcolor: 'backgroundColors.secondary',
          borderTop: '1px solid',
          borderColor: 'borderColors.primary',
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'textColors.lightText', 
            fontSize: '0.6875rem', 
            fontStyle: 'italic',
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          Source: <strong style={{ color: 'inherit' }}>{rule.source_document}</strong>
          {rule.source_date && ` • ${rule.source_date}`}
        </Typography>
      </Box>
    </CardWrapper>
  );
};
